// ============================================================
// sheetEdit - one edit to a live combatant's SHEET, applied whole.
//
// Editing the sheet of someone already in a fight touches all three
// halves of the combatant: the sheet takes the patch, the pins are
// rebased onto what the sheet now resolves to (see pins.ts), and the
// live state follows where it was still pristine (a suit of armour
// nobody has damaged yet is the suit on the sheet).
//
// Pure. A half that did not change is returned by IDENTITY, so the
// projection caches and both write clocks stay still.
// ============================================================
import type { Character, DefenderSheet } from '../character/types';
import { sameValue as same } from '../canonicalJson';
import { cleanedAfterTraitEdit, type CondFlash } from '../traitCleanup';
import { cachedDefenderSheet, projectDefender } from './projection';
import { rebasePins, resetPin, type PinRef } from './pins';
import { backfillSheetFromPins } from './backfill';
import type { Combatant, CombatState } from './types';

/** The three halves of a combatant an edit may replace. */
export type CombatantHalves = Pick<Combatant, 'sheet' | 'overrides' | 'state'>;

/** Each armour location still at its old pristine value follows the new one; worn ones keep their wear. */
function rebasedArmour(worn: CombatState['armour'], was: DefenderSheet['armour'], now: DefenderSheet['armour']) {
  const next = Object.fromEntries(
    Object.keys(now).map((loc) => [loc, worn[loc] === undefined || worn[loc] === was[loc] ? now[loc] : worn[loc]]),
  );
  return next as CombatState['armour'];
}

/**
 * The live state after the pristine sheet values under it moved: armour
 * location by location, and a force field nobody has overloaded.
 */
export function rebaseWorn(state: CombatState, was: DefenderSheet, now: DefenderSheet): CombatState {
  const armour = same(was.armour, now.armour)
    ? state.armour
    : rebasedArmour(state.armour ?? {}, was.armour, now.armour);
  const fieldMoved = !same(was.forceField, now.forceField) && same(state.forceField, was.forceField);
  const forceField = fieldMoved ? { ...now.forceField } : state.forceField;
  if (same(armour, state.armour) && forceField === state.forceField) return state;
  return { ...state, armour, forceField };
}

/**
 * The combatant's three halves after `patch` is laid over its sheet.
 *
 * The live state is rebased against what the table sees as pristine: the
 * sheet's answer with the pins over it, before and after the edit.
 */
export function applySheetEdit(c: Combatant, patch: Partial<Character>): CombatantHalves {
  if (changesNothing(c.sheet, patch)) return { sheet: c.sheet, overrides: c.overrides, state: c.state };
  const sheet: Character = { ...c.sheet, ...patch };
  const was = cachedDefenderSheet(c.sheet);
  const now = cachedDefenderSheet(sheet);
  const overrides = rebasePins(c.overrides ?? {}, was, now);
  const state = rebaseWorn(c.state, { ...was, ...c.overrides }, { ...now, ...overrides });
  return { sheet, overrides, state };
}

/**
 * Does the patch say only what the sheet already says? This is what
 * keeps the header's promise - a half that did not change comes back by
 * identity - since a new sheet object advances the write clock and
 * empties every projection cache. Such patches are common: a number
 * field re-emits its value on blur, Escape puts a value back, a switch
 * may be set to what it already is.
 */
function changesNothing(sheet: Character, patch: Partial<Character>): boolean {
  const current = sheet as unknown as Record<string, unknown>;
  return Object.entries(patch).every(([key, value]) => same(value, current[key]));
}

/**
 * The combatant with its overrides replaced, as a whole edit: the live
 * state follows the pristine values that moved (an undamaged plate under
 * a released armour pin), and an immunity that comes back strips what it
 * rules out. The halves by identity when the overrides say the same.
 */
function withOverrides(c: Combatant, overrides: Combatant['overrides']): CombatantHalves {
  if (same(overrides, c.overrides)) return { sheet: c.sheet, overrides: c.overrides, state: c.state };
  const resolved = cachedDefenderSheet(c.sheet);
  const state = rebaseWorn(c.state, { ...resolved, ...c.overrides }, { ...resolved, ...overrides });
  return withImmunitiesApplied(c, { sheet: c.sheet, overrides, state }).halves;
}

/** The combatant with some pins let go. */
export function releasePins(c: Combatant, refs: PinRef[]): CombatantHalves {
  const kept = refs.reduce((left, ref) => resetPin({ ...c, overrides: left }, ref.key, ref.leaf), c.overrides ?? {});
  return withOverrides(c, kept);
}

/**
 * The combatant with one value pinned over its sheet: what only the
 * table can say (an extra dodge nobody's talent grants). A leaf is laid
 * over what the sheet resolves for its siblings; a value that agrees
 * with the sheet is no pin, and is let go instead.
 *
 * Judged against the sheet AS SHOWN: a statblock is folded first, like
 * every other write the sheet makes.
 */
export function setPin(c: Combatant, ref: PinRef, value: unknown): CombatantHalves {
  const ready = readyForSheetEdit(c);
  const resolved = cachedDefenderSheet(ready.sheet) as unknown as Record<string, unknown>;
  const current = (ready.overrides ?? {}) as Record<string, unknown>;
  const whole = ref.leaf ? { ...((current[ref.key] ?? resolved[ref.key]) as object), [ref.leaf]: value } : value;
  const overrides = { ...current, [ref.key]: whole } as Combatant['overrides'];
  const pinned = { ...ready, overrides };
  // Through resetPin when it agrees, so a leaf that says what the sheet says is pruned.
  return withOverrides(ready, same(whole, resolved[ref.key]) ? resetPin(pinned, ref.key) : overrides);
}

/**
 * The combatant as the live sheet SHOWS and edits it: a statblock with
 * its pins folded into its sheet (see backfill.ts), anyone else as they
 * are. Deterministic, so the sheet an editor drew its patch from is the
 * sheet the patch lands on.
 */
export function readyForSheetEdit(c: Combatant): Combatant {
  if (c.kind !== 'statblock') return c;
  const folded = backfillSheetFromPins(c);
  return folded.sheet === c.sheet ? c : { ...c, ...folded };
}

/**
 * A sheet edit made on someone already in the fight - the one entry
 * point the live sheet writes through.
 *
 * A statblock is folded FIRST, lazily and in the same write: the patch
 * was drawn from the folded sheet, and only the client making the edit
 * rewrites it (a migration on load would have every client rewrite
 * every statblock at once).
 *
 * @param alsoState a change to the live state that belongs to the same
 *   edit (a force field switched on is on the sheet AND live), applied
 *   after the state has followed the sheet.
 * @param release pins the edit lets go first: a control that SHOWS a
 *   pinned value must be obeyed when it is set, even to what the sheet
 *   already says.
 */
export function editLiveSheet(
  c: Combatant,
  patch: Partial<Character>,
  alsoState?: (state: CombatState) => CombatState,
  release: PinRef[] = [],
): CombatantHalves {
  const ready = readyForSheetEdit(c);
  // Nothing to do at all: leave even a statblock exactly as it is, unfolded.
  if (!alsoState && release.length === 0 && changesNothing(ready.sheet, patch)) {
    return { sheet: c.sheet, overrides: c.overrides, state: c.state };
  }
  const released = release.length ? { ...ready, ...releasePins(ready, release) } : ready;
  const halves = applySheetEdit(released, patch);
  const edited = alsoState ? { ...halves, state: alsoState(halves.state) } : halves;
  return withImmunitiesApplied(ready, edited).halves;
}

/**
 * The edit with the conditions a newly gained immunity rules out taken
 * off: a trait or talent added on the sheet strips what it is immune to,
 * exactly as switching the flat trait on always did.
 */
function withImmunitiesApplied(
  before: Combatant,
  halves: CombatantHalves,
): { halves: CombatantHalves; flash: CondFlash | null } {
  // Nothing to strip from someone who carries no conditions: skip two projections.
  if (!halves.state.conditions?.length) return { halves, flash: null };
  const was = projectDefender(before);
  const now = projectDefender({ ...before, ...halves });
  const { target, flash } = cleanedAfterTraitEdit(was, now);
  if (target === now) return { halves, flash: null };
  return { halves: { ...halves, state: { ...halves.state, conditions: target.conditions } }, flash };
}

/** What an edit would strip, to say so: "Pinned removed by From Beyond". Null when nothing. */
export function sheetEditFlash(c: Combatant, patch: Partial<Character>): CondFlash | null {
  // Only someone carrying conditions has anything to strip; most edits end here.
  if (!c.state.conditions?.length) return null;
  const ready = readyForSheetEdit(c);
  return withImmunitiesApplied(ready, applySheetEdit(ready, patch)).flash;
}
